import React, { useEffect, useMemo, useState } from "react";
import SummaryCards from "../../components/Common/SummaryCards";
import {
  Modal,
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
} from "../../components/Common/AppOverlay";
import ActionMenu from "../../components/Common/ActionMenu";
import useAdminDataLifecycle from "../../components/Common/useAdminDataLifecycle";
import { showLoading } from "react-global-loading";
import { ErrorNotify } from "../../shared/Alert";
import { getChecklists } from "../../helpers/backend_helper";
import CustomDataGrid from "../../components/Common/CustomDataGrid";
import ChecklistDetail from "./ChecklistDetail";
import ChecklistFullDetail from "./ChecklistFullDetail";
import ChecklistPreview from "./ChecklistPreview";
import "../Standards/Standards.scss";

const itemNames = (items, key) =>
  (items || [])
    .map((item) => item?.[key]?.name || item?.name)
    .filter(Boolean)
    .join(", ") || "—";

const statusInfo = (status) =>
  status === 1
    ? { label: "Active", tone: "active" }
    : status === 2
    ? { label: "Archived", tone: "archived" }
    : { label: "Draft", tone: "draft" };

const ChecklistActionMenu = ({
  checklist,
  onEdit,
  onPreview,
  onQuestions,
  lifecycleActions,
}) => {
  return (
    <ActionMenu
      label={checklist.name}
      actions={[
        {
          label: "Preview Checklist",
          icon: <i className="bx bx-show" />,
          onSelect: () => onPreview(checklist),
        },
        {
          label: "Edit details",
          icon: <i className="bx bx-pencil" />,
          onSelect: () => onEdit(checklist.id),
        },
        {
          label: "View questions",
          icon: <i className="bx bx-list-ul" />,
          onSelect: () => onQuestions(checklist),
        },
        ...lifecycleActions,
      ]}
    />
  );
};

const CheckLists = ({ createRequest = 0, refreshRequest = 0 }) => {
  const [checklists, setChecklists] = useState([]);
  const [selectedId, setSelectedId] = useState(0);
  const [selectedChecklist, setSelectedChecklist] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isFullDetailOpen, setIsFullDetailOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewChecklist, setPreviewChecklist] = useState(null);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [standard, setStandard] = useState("all");
  const [workType, setWorkType] = useState("all");

  const loadData = () => {
    showLoading(true);
    getChecklists(true)
      .then((response) => {
        if (!response?.status) {
          throw new Error("Unable to load checklists. Please try again.");
        }
        setChecklists(response?.data || []);
      })
      .catch((error) => {
        console.error(error);
        ErrorNotify("Error occurred while fetching checklists.");
      })
      .finally(() => showLoading(false));
  };
  const lifecycle = useAdminDataLifecycle(loadData);

  const openDetail = (id = 0) => {
    setSelectedId(id);
    setIsDetailOpen(true);
  };

  const closeDetail = () => {
    setIsDetailOpen(false);
    setSelectedId(0);
    loadData();
  };

  const openQuestions = (checklist) => {
    setSelectedChecklist(checklist);
    setSelectedId(checklist.id);
    setIsFullDetailOpen(true);
  };

  const openPreview = (checklist) => {
    setPreviewChecklist(checklist);
    setIsPreviewOpen(true);
  };

  const closePreview = () => {
    setIsPreviewOpen(false);
    setPreviewChecklist(null);
  };

  const closeQuestions = () => {
    setIsFullDetailOpen(false);
    setSelectedChecklist(null);
    setSelectedId(0);
    loadData();
  };
  useEffect(() => {
    loadData();
  }, [refreshRequest]);
  useEffect(() => {
    if (createRequest) openDetail();
  }, [createRequest]);

  const standards = useMemo(
    () =>
      Array.from(
        new Map(
          checklists
            .filter((item) => item.standardType?.id)
            .map((item) => [item.standardType.id, item.standardType])
        ).values()
      ),
    [checklists]
  );
  const workTypes = useMemo(
    () =>
      Array.from(
        new Set(
          checklists.flatMap((item) =>
            (item.workTypes || [])
              .map((work) => work.workType?.name || work.name)
              .filter(Boolean)
          )
        )
      ),
    [checklists]
  );
  const counts = useMemo(
    () => ({
      total: checklists.length,
      active: checklists.filter((item) => item.status === 1).length,
      draft: checklists.filter((item) => item.status === 0).length,
      archived: checklists.filter((item) => item.status === 2).length,
    }),
    [checklists]
  );
  const filteredChecklists = useMemo(
    () =>
      checklists.filter((item) => {
        const searchText = `${item.name || ""} ${item.description || ""} ${
          item.standardType?.name || ""
        }`.toLowerCase();
        const hasWorkType =
          workType === "all" ||
          (item.workTypes || []).some(
            (work) => (work.workType?.name || work.name) === workType
          );
        return (
          (!search || searchText.includes(search.toLowerCase())) &&
          (status === "all" || String(item.status) === status) &&
          (standard === "all" || String(item.standardType?.id) === standard) &&
          hasWorkType
        );
      }),
    [checklists, search, status, standard, workType]
  );

  const resetFilters = () => {
    setSearch("");
    setStatus("all");
    setStandard("all");
    setWorkType("all");
  };
  const columns = [
    {
      id: "name",
      header: "Name",
      accessorKey: "name",
      minSize: 190,
      flex: 1.2,
      cell: ({ row, getValue }) => (
        <button
          type="button"
          className="standards-grid__name"
          style={{ fontSize: 13 }}
          onClick={() => openQuestions(row.original)}
        >
          <span>{(getValue() || "C").charAt(0)}</span>
          {getValue()}
        </button>
      ),
    },
    {
      id: "standard",
      header: "Standard",
      accessorFn: (row) => row.standardType?.name || "—",
      minSize: 150,
      flex: 0.9,
    },
    {
      id: "description",
      header: "Description",
      accessorKey: "description",
      minSize: 240,
      flex: 1.5,
      cell: ({ getValue }) => (
        <span className="standards-grid__description">{getValue() || "—"}</span>
      ),
    },
    {
      id: "workTypes",
      header: "Work Types",
      accessorFn: (row) => itemNames(row.workTypes, "workType"),
      minSize: 170,
      flex: 1,
    },
    {
      id: "industryTypes",
      header: "Industry Types",
      accessorFn: (row) => itemNames(row.industryTypes, "industryType"),
      minSize: 180,
      flex: 1,
    },
    {
      id: "reviewTypes",
      header: "Review Types",
      accessorFn: (row) => itemNames(row.reviewTypes, "reviewType"),
      minSize: 170,
      flex: 1,
    },
    {
      id: "status",
      header: "Status",
      accessorFn: (row) => statusInfo(row.status).label,
      minSize: 110,
      flex: 0.7,
      cell: ({ row, getValue }) => (
        <span
          className={`standards-grid__status standards-grid__status--${
            statusInfo(row.original.status).tone
          }`}
        >
          {getValue()}
        </span>
      ),
    },
    {
      id: "actions",
      header: "Actions",
      accessorFn: () => "",
      minSize: 80,
      flex: 0,
      enableSorting: false,
      enableColumnFilter: false,
      enableExport: false,
      cell: ({ row }) => (
        <ChecklistActionMenu
          checklist={row.original}
          onEdit={openDetail}
          onPreview={openPreview}
          onQuestions={openQuestions}
          lifecycleActions={lifecycle.actions("checklist", row.original)}
        />
      ),
    },
  ];

  return (
    <section className="standards-page__content">
      <SummaryCards
        items={[
          [
            "bx-list-check",
            "blue",
            "Total Checklists",
            counts.total,
            "All checklists",
          ],
          [
            "bx-check-circle",
            "green",
            "Active Checklists",
            counts.active,
            `${
              counts.total
                ? Math.round((counts.active / counts.total) * 100)
                : 0
            }% of total`,
          ],
          [
            "bx-time-five",
            "orange",
            "Draft Checklists",
            counts.draft,
            `${
              counts.total ? Math.round((counts.draft / counts.total) * 100) : 0
            }% of total`,
          ],
          [
            "bx-archive-in",
            "red",
            "Archived Checklists",
            counts.archived,
            `${
              counts.total
                ? Math.round((counts.archived / counts.total) * 100)
                : 0
            }% of total`,
          ],
        ]}
        ariaLabel="Checklist summary"
      />
      <div>
        
        <CustomDataGrid
 filterContent={<section
          className="standards-filter-panel"
          aria-label="Checklist filters"
        >
          <div className="standards-filter-panel__fields">
            <label>
              <span>Search</span>
              <div className="standards-filter-panel__search">
                <i className="bx bx-search" />
                <input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search checklists..."
                />
              </div>
            </label>
            <label>
              <span>Standard</span>
              <select
                value={standard}
                onChange={(event) => setStandard(event.target.value)}
              >
                <option value="all">All Standards</option>
                {standards.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>Work Type</span>
              <select
                value={workType}
                onChange={(event) => setWorkType(event.target.value)}
              >
                <option value="all">All Work Types</option>
                {workTypes.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>Status</span>
              <select
                value={status}
                onChange={(event) => setStatus(event.target.value)}
              >
                <option value="all">All Statuses</option>
                <option value="1">Active</option>
                <option value="0">Draft</option>
                <option value="2">Archived</option>
              </select>
            </label>
          </div>
          <div className="standards-filter-panel__actions">
            <button type="button" onClick={resetFilters}>Clear</button>
          </div>
        </section>}

          data={filteredChecklists}
          columns={columns}
          onRefresh={loadData}
          fileName="checklists"
          recordLabel="checklists"
          showToolbar={false}
          initialPageSize={15}
          rowHeight={56}
          columnHeaderHeight={56}
          gridSx={{
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#ffffff !important",
              borderBottom: "1px solid #e7ebf2",
            },
            "& .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeader--sortable":
              { backgroundColor: "#ffffff !important", color: "#52627f" },
            "& .MuiDataGrid-columnHeaderTitle": {
              color: "#52627f",
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: ".2px",
            },
            "& .MuiDataGrid-columnSeparator": { color: "#e7ebf2" },
            "& .MuiDataGrid-cell": {
              fontSize: 13,
              borderBottomColor: "#edf0f5",
              color: "#273957",
            },
            "& .MuiDataGrid-row": { minHeight: "56px !important" },
            "& .MuiDataGrid-footerContainer, & .MuiDataGrid-footerContainer .MuiTypography-root":
              { fontSize: 14, borderTopColor: "#e7ebf2" },
          }}
        />
      </div>
      <Offcanvas
        backdrop="static"
        className="standards-drawer"
        direction="end"
        isOpen={isDetailOpen}
        toggle={closeDetail}
      >
        <OffcanvasHeader toggle={closeDetail} className="canvasHeader">
          {selectedId ? "Edit Checklist" : "New Checklist"}
        </OffcanvasHeader>
        <OffcanvasBody>
          <ChecklistDetail detailId={selectedId} closeAction={closeDetail} />
        </OffcanvasBody>
      </Offcanvas>
      <Modal
        isOpen={isPreviewOpen}
        toggle={closePreview}
        fullscreen
        modalClassName="checklist-preview-modal-root"
        contentClassName="checklist-preview-modal"
      >
        <ChecklistPreview
          checklist={previewChecklist}
          checklistId={previewChecklist?.id}
          onClose={closePreview}
          modal
        />
      </Modal>
      <Modal
        isOpen={isFullDetailOpen}
        toggle={closeQuestions}
        fullscreen
        modalClassName="checklist-questions-modal-root"
        contentClassName="checklist-questions-modal"
      >
        <section className="checklist-questions-workspace">
          <header className="checklist-questions-workspace__header">
            <h2>Checklist Details: {selectedChecklist?.name || ""}</h2>
            <button
              type="button"
              className="checklist-questions-workspace__close"
              onClick={closeQuestions}
              aria-label="Close checklist details"
            >
              <i className="bx bx-x" />
            </button>
          </header>
          <main className="checklist-questions-workspace__body piers-page">
            <ChecklistFullDetail
              detailId={selectedId}
              dataDetail={selectedChecklist}
              closeAction={closeQuestions}
            />
          </main>
          <footer className="checklist-questions-workspace__footer">
            <button type="button" onClick={closeQuestions}>
              Close
            </button>
          </footer>
        </section>
      </Modal>
      {lifecycle.dialog}
    </section>
  );
};

export default CheckLists;
